import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { ErrorViewProps } from '../../../common/views/ErrorView/props';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingBlockProps {
  title: string;
  /** A count or a note right of the title. */
  meta?: React.ReactNode;
  /** "All tracks": the link to the full section. */
  moreLabel?: string;
  onMore?: () => void;
  isLoading?: boolean;
  /** Set when the read of this block failed: the block shows the ErrorView only (design f5). */
  error?: ErrorViewProps['error'];
  retry?: () => void;
  testId?: string;
  /** The block sits inside another block: no shadow (a box inside a box). */
  nested?: boolean;
  children?: React.ReactNode;
}

/** The frame of a Streaming block. Each block loads and fails by itself (design f5). */
const StreamingBlockComponent: React.FC<StreamingBlockProps> = ({
  title,
  meta,
  moreLabel,
  onMore,
  isLoading = false,
  error,
  retry,
  testId,
  nested = false,
  children,
}) => (
  <Panel as="section" nested={nested} className={styles.block} data-testid={testId}>
    <PanelHead
      titleAs="h2"
      title={title}
      right={
        meta !== undefined || (moreLabel !== undefined && onMore !== undefined && error === undefined) ? (
          <>
            {meta !== undefined ? <span className={styles.faint}>{meta}</span> : null}
            {moreLabel !== undefined && onMore !== undefined && error === undefined ? (
              <button type="button" className={styles.linkButton} onClick={onMore}>
                {moreLabel} <FontAwesomeIcon icon={faChevronRight} />
              </button>
            ) : null}
          </>
        ) : undefined
      }
    />
    <PanelBody stack className={styles.blockBody}>
      {isLoading ? <LoadingView /> : null}
      {!isLoading && error !== undefined ? <ErrorView error={error} retry={retry} /> : null}
      {!isLoading && error === undefined ? children : null}
    </PanelBody>
  </Panel>
);

export const StreamingBlock = TypedMemo(StreamingBlockComponent);
